import { motion } from "framer-motion";
import { Target, Eye, HeartHandshake, Leaf, Truck, BadgeCheck, Package, Users } from "lucide-react";
import { fadeUp, stagger, viewport } from "../lib/motion";
import Breadcrumb from "../components/site/Breadcrumb";
import CTABand from "../components/site/CTABand";

const WHY = [
  { icon: BadgeCheck, t: "Uncompromising quality", d: "Every product is food-grade and quality-checked before dispatch." },
  { icon: Package, t: "200+ product lines", d: "One partner for paper, sugarcane, plastic, aluminium & cutlery." },
  { icon: Truck, t: "Reliable bulk supply", d: "Deep inventory and on-time delivery across Tamil Nadu & India." },
  { icon: Leaf, t: "Sustainable range", d: "Compostable bagasse, paper and wooden alternatives in stock." },
  { icon: HeartHandshake, t: "Custom branding", d: "Turn packaging into marketing with custom-printed designs." },
  { icon: Users, t: "Dedicated support", d: "Helpful account managers who understand foodservice." },
];

export default function About() {
  return (
    <div>
      <section className="bg-[#F8FAF8] border-b border-[#E2E8E4] pt-12 pb-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <Breadcrumb items={[{ label: "About" }]} />
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mt-6 mb-3">
            About Chennai Packs
          </p>
          <h1 className="font-head text-4xl md:text-6xl font-medium tracking-tighter text-[#0A1C0E] max-w-4xl">
            A packaging partner built for Indian food businesses
          </h1>
          <p className="font-editorial text-2xl text-[#3A4D3E] mt-6 max-w-2xl">
            From a single carton to a full truckload — we deliver quality, scale and sustainability.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-head text-3xl md:text-4xl font-medium text-[#0A1C0E]">Who we are</h2>
            <div className="space-y-5 mt-6 text-lg text-[#3A4D3E] leading-relaxed">
              <p>
                Chennai Packs is a premium food packaging supplier serving restaurants, cloud
                kitchens, hotels, caterers, wholesalers and retailers across India. We help food
                businesses package their products safely, sustainably and beautifully.
              </p>
              <p>
                With 200+ product lines spanning paper, sugarcane bagasse, PP, aluminium and cutlery,
                we are a single, dependable source for everything a modern kitchen needs — backed by
                deep inventory and fast, reliable delivery.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 space-y-5">
            <div className="bg-[#1D6B2A] text-white rounded-2xl p-7">
              <Target size={28} className="text-[#FDCD00]" />
              <h3 className="font-head text-xl font-semibold mt-4">Our mission</h3>
              <p className="text-white/75 mt-2">
                To make premium, sustainable packaging accessible and affordable for every food
                business in India.
              </p>
            </div>
            <div className="bg-white border border-[#E2E8E4] rounded-2xl p-7">
              <Eye size={28} className="text-[#1D6B2A]" />
              <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-4">Our vision</h3>
              <p className="text-[#3A4D3E] mt-2">
                To be the most trusted packaging partner for foodservice brands nationwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#F8FAF8]" id="why-choose-us">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#1D6B2A] mb-3">Why choose us</p>
            <h2 className="font-head text-4xl md:text-5xl font-medium tracking-tight text-[#0A1C0E]">
              Six reasons brands stay with us
            </h2>
          </div>
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {WHY.map((w, i) => (
              <motion.div
                key={w.t}
                variants={fadeUp}
                custom={i}
                className="bg-white border border-[#E2E8E4] rounded-2xl p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-500"
              >
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#F8FAF8] text-[#1D6B2A]">
                  <w.icon size={24} />
                </span>
                <h3 className="font-head text-xl font-semibold text-[#0A1C0E] mt-4">{w.t}</h3>
                <p className="text-[#3A4D3E] mt-2">{w.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
